import {
  createCamelCasedClient,
  createTokenRefreshInterceptor,
  getContentLibrary,
  getCreatorSegments,
  getPerformance,
} from '@metapic-apps/shared'
import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { config } from '@/lib/helpers'
import { observeAdBlockerFailure } from '@/lib/services/ad-blocker.service'
import { notifyUserBanned } from '@/lib/services/banned-user.service'
import { useAuthStore } from '@/stores/auth.stores'

const socialMediaAxios = axios.create({
  baseURL: config.socialMediaApiUrl,
})

if (import.meta.env.VITE_DEMO_MODE === 'true') {
  const { createDemoAdapter } = await import('@/demo/demo-adapter')
  socialMediaAxios.defaults.adapter = createDemoAdapter()
}

socialMediaAxios.interceptors.request.use(
  (request) => {
    const authStore = useAuthStore()

    request.headers.setAuthorization(authStore.authorization || '')
    request.headers.setContentType(request.isFormData ? 'multipart/form-data' : 'application/json')

    return request
  },
  (error: AxiosError) => Promise.reject(error),
)

socialMediaAxios.interceptors.response.use(
  (response) => response,
  (
    error: AxiosError<{
      code?: string
      message?: string
    }>,
  ) => {
    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      notifyUserBanned()
    }

    void observeAdBlockerFailure(error)
    return Promise.reject(error)
  },
)

createTokenRefreshInterceptor(socialMediaAxios, {
  refreshToken: () => useAuthStore().refreshToken(),
  onRefreshFailure: () => useAuthStore().handleRefreshFailure(),
})

const socialMediaClientFactory = (axiosInstance: AxiosInstance) => ({
  ...getContentLibrary(axiosInstance),
  ...getPerformance(axiosInstance),
})

export const socialMediaClient = {
  ...createCamelCasedClient(socialMediaClientFactory, socialMediaAxios),
  creatorSegments: createCamelCasedClient(getCreatorSegments, socialMediaAxios),
}
